import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useQuery } from "@tanstack/react-query";

export interface UserUsageHistoryItem {
  reservationSeq: number;
  petSeq: number;
  companySeq: number | null;
  fileUrl: string | null;
  petName: string;
  companyName: string | null;
  productName: string | null;
  cdReservType:
    | "RESERV_TYPE_CLASS"
    | "RESERV_TYPE_DAYCARE"
    | "RESERV_TYPE_HOTEL"
    | "RESERV_TYPE_GROOMING"
    | "RESERV_TYPE_TRAINING"
    | string;
  dtReservStart: string | null;
  dtReservEnd: string | null;
  startTime: string | null;
  endTime: string | null;
  checkInTime: string | null;
  checkOutTime: string | null;
  durationText: string | null;
  ticketName: string | null;
}

export interface UserUsageHistoryRequest {
  searchKeyword?: string;
  startDate?: string;
  endDate?: string;
}

export interface UserUsageHistoryResponse {
  statusCode: number;
  data: UserUsageHistoryItem[];
  message?: string;
}

export interface UserUsageDetail {
  reservationSeq: number;
  petName: string;
  petThumbnailUrl: string | null;
  companyName: string | null;
  companySeq: number | null;
  reservation: string | null;
  cdReservType: string | null;
  dtReservStart: string | null;
  dtReservEnd: string | null;
  startTime: string | null;
  endTime: string | null;
  everyWeek: boolean;
  checkInTime: string | null;
  checkOutTime: string | null;
  durationText: string | null;
  memo: string | null;
  ticketName: string | null;
  noticeInfo: {
    send: boolean;
    notiRecvSeq: number | null;
  };
}

export interface UserUsageDetailResponse {
  statusCode: number;
  data: UserUsageDetail;
  message?: string;
}

const USER_USAGE_HISTORY_QUERY_KEYS = {
  list: ["user", "mypage", "usage-history"],
  detail: ["user", "mypage", "usage-detail"],
} as const;

const getUserUsageHistory = async (
  params: UserUsageHistoryRequest
): Promise<UserUsageHistoryResponse> => {
  const { data } = await privateHttp.get(
    API_ENDPOINTS.USER_MYPAGE.USAGE_HISTORY,
    {
      params: {
        searchKeyword: params.searchKeyword,
        startDate: params.startDate,
        endDate: params.endDate,
      },
    }
  );
  return data;
};

export const useUserUsageHistory = (
  params: UserUsageHistoryRequest,
  options?: { enabled?: boolean; refetchKey?: number }
) => {
  return useQuery({
    queryKey: [
      ...USER_USAGE_HISTORY_QUERY_KEYS.list,
      params,
      options?.refetchKey,
    ],
    queryFn: () => getUserUsageHistory(params),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
  });
};

const getUserUsageDetail = async (
  reservationSeq: number
): Promise<UserUsageDetailResponse> => {
  const { data } = await privateHttp.get(
    API_ENDPOINTS.USER_MYPAGE.USAGE_DETAIL,
    { params: { reservationSeq } }
  );
  return data;
};

export const useUserUsageDetail = (
  reservationSeq: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [...USER_USAGE_HISTORY_QUERY_KEYS.detail, reservationSeq],
    queryFn: () => getUserUsageDetail(reservationSeq),
    enabled: (options?.enabled ?? true) && reservationSeq > 0,
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
  });
};
